import {
  Box,
  Button,
  IconButton,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import { LoadingButton } from "@mui/lab";
import { authState } from "src/store/auth";
import { useUpdateCompanyData } from "src/api/generic";

interface IProps {
  open: boolean;
  handleClose: () => void;
  refetch?: Function;
}

const schema = yup.object().shape({
  foodicsCompanyId: yup.string().required("required"),
});

export interface FormInputs extends yup.InferType<typeof schema> {}

const FoodicsCompanyIdPopup = (props: IProps) => {
  const { open, handleClose, refetch = () => {} } = props;
  const { t } = useTranslation("profile");
  const { t: generalT } = useTranslation("general");
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [auth, setAuth] = useRecoilState(authState);
  const { user } = auth;

  // APIS
  const { mutate, status, error, isPending } = useUpdateCompanyData();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    getValues,
  } = useForm<FormInputs>({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: {
      foodicsCompanyId: user.company?.close_date,
    },
  });

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    const { foodicsCompanyId } = data;

    mutate({
      id: user.company.id,
      foodics_reference: foodicsCompanyId,
    });
  };

  //Update success and error handling
  useEffect(() => {
    if (status === "success") {
      const newAuth = {
        ...auth,
        user: {
          ...user,
          company: {
            ...user.company,
            foodics_reference: getValues("foodicsCompanyId"),
          },
        },
      };
      setAuth(newAuth);
      handleClose();
    } else if (status === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: error?.data.errors?.[0].value ?? generalT("somethingWrong"),
        },
      ]);
    }
  }, [status]);

  useEffect(() => {
    reset({ foodicsCompanyId: user.company.foodics_reference });
  }, [user]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Stack
        spacing={3}
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 537,
          bgcolor: "#FFF",
          border: "1px solid #D0D5DD",
          borderRadius: 1,
          boxShadow: 24,
          p: 3,
        }}
      >
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
          p={0}
        >
          <Typography color={"#101828"} fontSize={"20px"}>
            {t("foodicsCompanyId")}
          </Typography>
          <IconButton onClick={handleClose} sx={{ p: 0 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            <Controller
              name="foodicsCompanyId"
              control={control}
              render={({ field }) => (
                <TextField
                  label={t("foodicsCompanyId")}
                  fullWidth
                  size="small"
                  {...field}
                  error={!!errors.foodicsCompanyId}
                  helperText={errors.foodicsCompanyId?.message}
                />
              )}
            />
          </Stack>
          <Stack mt={3} spacing={2} direction={"row"}>
            <Button
              variant="outlined"
              color="tertiary"
              fullWidth
              onClick={handleClose}
            >
              {generalT("cancel")}
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              fullWidth
              loading={isPending}
            >
              {generalT("submit")}
            </LoadingButton>
          </Stack>
        </form>
      </Stack>
    </Modal>
  );
};

export default FoodicsCompanyIdPopup;
